import { AnalystComment } from '../Components/Shared/AnalystCommentsEditor/AnalystCommentsEditor';

/** Items whose label contains the typed text (trimmed, case-insensitive). Blank text keeps everything. */
export const filterByLabel = <T>(
  items: T[],
  text: string | null | undefined,
  getLabel: (item: T) => string | null | undefined,
): T[] => {
  const needle = (text ?? '').trim().toLowerCase();
  if (needle.length === 0) {
    return items;
  }
  return items.filter(item => (getLabel(item) ?? '').toLowerCase().includes(needle));
};

export const pluralize = (count: number, singular: string, plural: string = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

export interface DecisionSummaryCounts {
  /** Omit when the decision code does not require stipulations. */
  stipulationCount?: number;
  /** Omit when the decision code does not require adverse action reasons. */
  reasonCount?: number;
  commentCount: number;
}

/** One-line description of what the dealer will receive, e.g. "3 stipulations · 2 comments will be sent". */
export const buildDecisionSummary = (counts: DecisionSummaryCounts): string => {
  const parts: string[] = [];
  if (counts.stipulationCount !== undefined) {
    parts.push(pluralize(counts.stipulationCount, 'stipulation'));
  }
  if (counts.reasonCount !== undefined) {
    parts.push(pluralize(counts.reasonCount, 'reason'));
  }
  parts.push(pluralize(counts.commentCount, 'comment'));
  return `${parts.join(' · ')} will be sent`;
};

/**
 * The comments to send, with the decision code's default comment appended as a credit comment.
 * Pure: never mutates the typed comments, and idempotent so re-validating a submit cannot add it twice.
 */
export const withDefaultComment = (comments: AnalystComment[], defaultComment: string | undefined | null): AnalystComment[] => {
  if (!defaultComment) {
    return comments;
  }
  if (comments.some(c => c.type === 'credit' && c.comments === defaultComment)) {
    return comments;
  }
  return [...comments, { type: 'credit', comments: defaultComment }];
};
